// The admin browser's shape: show folder -> season folder -> episode rows,
// derived from the flat mapping list the API already returns.
//
// `GET /api/hosted/list` hands back one entry per key, and the key is the whole
// address: `movie:<id>` for a film, `tv:<id>:<season>:<episode>` for an episode.
// Folding on that is a *view*, not a second source of truth — nothing new is
// stored, nothing new is fetched, and a mapping edited anywhere else shows up
// here on the next load. A season folder therefore exists exactly as long as one
// of its episodes does; a season the operator has only just created is client
// state (AdminScreen keeps those numbers) and disappears on reload until
// something is added to it.
//
// Pure and dependency-free on purpose: `scripts/test-admin-folders.mjs` drives
// it straight from node, and the screen imports the same functions, so the tree
// the tests pin down is the tree that renders.

/** `movie:155` | `tv:900000001:8:1` -> `{ kind, id, season, episode }`, or null. */
export function parseHostedKey(key) {
  const raw = String(key || "").trim();
  const tv = /^tv:(\d+):(\d+):(\d+)$/.exec(raw);
  if (tv) return { kind: "tv", id: Number(tv[1]), season: Number(tv[2]), episode: Number(tv[3]) };
  const movie = /^movie:(\d+)$/.exec(raw);
  if (movie) return { kind: "movie", id: Number(movie[1]), season: null, episode: null };
  return null;
}

export function seasonLabel(season) {
  return `Season ${season}`;
}

/** `8, 1` -> `S08E01` — the label the operator reads on an episode row. */
export function episodeLabel(season, episode) {
  const s = String(season);
  const e = String(episode);
  return `S${s.length < 2 ? s.padStart(2, "0") : s}E${e.length < 2 ? e.padStart(2, "0") : e}`;
}

/** `1` -> 2 — the episode a fresh "Add" should offer next. */
export function nextEpisodeNumber(episodes) {
  const numbers = (episodes || []).map((e) => Number(e?.episode)).filter((n) => Number.isFinite(n));
  return numbers.length ? Math.max(...numbers) + 1 : 1;
}

/**
 * Every episode number a season shows, gaps included: a season holding 8:1, 8:2
 * and 8:7 lists S08E01..S08E07 so the holes are visible and one click away,
 * rather than pretending the season ends at 2.
 *
 * Takes entries of the shape `{ episode, mapping }` and hands each slot the
 * `mapping` back, so `slot.mapping` is null for a hole and the raw mapping for
 * a filled one. `label` is left to the caller — only buildHostedTree knows the
 * season.
 */
export function episodeSlots(episodes) {
  const byNumber = new Map();
  for (const e of episodes || []) {
    const n = Number(e?.episode);
    if (Number.isFinite(n) && !byNumber.has(n)) byNumber.set(n, e);
  }
  const highest = byNumber.size ? Math.max(...byNumber.keys()) : 0;
  const slots = [];
  for (let n = 1; n <= highest; n += 1) {
    const entry = byNumber.get(n);
    slots.push({ episode: n, label: null, mapping: entry ? entry.mapping ?? null : null });
  }
  return slots;
}

/**
 * Fold the flat list into folders.
 *
 * Returns `{ shows, movies, unparsed }`:
 *  - `shows`  — one entry per show id, seasons ascending, episodes ascending,
 *               each carrying `nextEpisode` and `slots` (see episodeSlots);
 *  - `movies` — the `movie:` entries, newest first, left exactly as they were:
 *               films have no season, so they stay a flat list until the
 *               operator says what a movie folder should look like;
 *  - `unparsed` — keys we do not recognise, kept so the screen can show them
 *               instead of silently dropping a mapping on the floor.
 *
 * `subtitles` is the other store's list (GET /api/subs/list). It is keyed the
 * same way — `movie:<id>` / `tv:<id>:<season>:<episode>` — so a title's subtitle
 * tracks can ride along with the file that plays it and the operator sees both in
 * one row instead of hunting a second card. Entries are cloned rather than
 * mutated: these objects come straight from a fetch response the caller still
 * owns.
 */
export function buildHostedTree(mappings, { subtitles = null } = {}) {
  const tracksByKey = new Map();
  for (const entry of subtitles || []) {
    if (entry?.key && Array.isArray(entry.tracks)) tracksByKey.set(entry.key, entry.tracks);
  }
  const withTracks = (mapping) => {
    const tracks = tracksByKey.get(mapping?.key);
    return tracks ? { ...mapping, tracks } : mapping;
  };

  const byShow = new Map();
  const movies = [];
  const unparsed = [];

  for (const mapping of mappings || []) {
    const parsed = parseHostedKey(mapping?.key);
    if (!parsed) {
      unparsed.push(mapping);
      continue;
    }
    if (parsed.kind === "movie") {
      movies.push(withTracks(mapping));
      continue;
    }
    let show = byShow.get(parsed.id);
    if (!show) {
      show = { id: parsed.id, title: String(mapping?.title || "").trim() || `Show ${parsed.id}`, seasons: new Map() };
      byShow.set(parsed.id, show);
    }
    // A later entry for the same show can carry the name the first one lacked.
    if (mapping?.title && String(mapping.title).trim() && show.title.startsWith("Show ")) {
      show.title = String(mapping.title).trim();
    }
    if (!show.seasons.has(parsed.season)) show.seasons.set(parsed.season, new Map());
    show.seasons.get(parsed.season).set(parsed.episode, withTracks(mapping));
  }


  const shows = [...byShow.values()].map((show) => {
    const seasons = [...show.seasons.keys()]
      .sort((a, b) => a - b)
      .map((season) => {
        const episodes = [...show.seasons.get(season).values()]
          .map((mapping) => ({ ...parseHostedKey(mapping.key), mapping }))
          .sort((a, b) => a.episode - b.episode);
        return {
          season,
          label: seasonLabel(season),
          episodes,
          count: episodes.length,
          nextEpisode: nextEpisodeNumber(episodes),
          // `slot.mapping` is the entry /hosted/list handed us, so the screen
          // reads `slot.mapping.links` and a gap is simply `null`. Built from
          // `episodes` so the numbers and the links can never disagree.
          slots: episodeSlots(episodes.map((e) => ({ episode: e.episode, mapping: e.mapping }))).map((slot) => ({
            ...slot,
            label: episodeLabel(season, slot.episode),
          })),
        };
      });
    return {
      id: show.id,
      title: show.title,
      seasons,
      seasonCount: seasons.length,
      episodeCount: seasons.reduce((n, s) => n + s.count, 0),
    };
  });
  shows.sort((a, b) => a.title.localeCompare(b.title));

  movies.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));

  return { shows, movies, unparsed };
}

/**
 * The season numbers a show should list: the ones it has episodes for, plus any
 * the operator has created but not filled yet. Sorted, de-duplicated, so the
 * folder list can never show "Season 8" twice.
 */
export function seasonNumbers(show, extra = []) {
  const fromData = (show?.seasons || []).map((s) => s.season);
  const all = [...fromData, ...(extra || []).map(Number).filter((n) => Number.isFinite(n) && n > 0)];
  return [...new Set(all)].sort((a, b) => a - b);
}
